import PrintIcon from '@mui/icons-material/Print'
import { Box, Typography } from '@mui/material'
import React, { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  FetchLicensedDirectorBillingDetailsResponse,
  paymentsApi,
} from '../../../../api/swagger'
import { useAppSnackbar } from '../../../../hooks/useAppSnackbar'
import useLoadingContext from '../../../../hooks/useLoadingContext'
import { useLoadingIds } from '../../../../hooks/useLoadingIds'
import fullName from '../../../../utils/fullName'
import ContainedButton, {
  ContainedButtonVariant,
} from '../../../Buttons/ContainedButton'
import IconTextButton, {
  IconTextButtonVariant,
} from '../../../Buttons/IconTextButton'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '../../../Buttons/OutlinedButton'
import ActionButtons from '../../../Buttons/ActionButtons'
import Header from '../../../Elements/Header'
import SpaceBetweenSection from '../../../Elements/SpaceBetweenSection'
import PaymentModal, { PaymentModalVariant } from '../../../Modals/PaymentModal'
import BasicModal from '../../../Modals/BasicModal'
import { ActionableTableSkeleton } from '../../../Skeleton/ActionableTableSkeleton'
import LCDLicensingBreakdownDetails from './Details'
import LCDLicensingBreakdownPreview from './Preview'
import LCDLicensingBreakdownTutor from './Tutor'
import {
  convertTwoDecimalStringToBig,
  formatBigFixedTwoDecimals,
} from '../../../../utils/bigJSHelper'

interface LCDLicensingBreakdownProps {
  programName: string
  communityName: string
  programKey: number
  achDiscount: string
  billingDataLoadingId: string
  achAllowed?: boolean
}

const LCDLicensingBreakdown: FC<LCDLicensingBreakdownProps> = ({
  programName,
  communityName,
  programKey,
  achDiscount,
  billingDataLoadingId,
  achAllowed = true,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { handleError } = useAppSnackbar()
  const {
    BillingTab: {
      fetchLicensedDirectorBillingDetails:
        fetchLicensedDirectorBillingDetailsLoadingId,
    },
    PaymentApi: {
      fetchLicensingLcdPayments: fetchLicensingLcdPaymentsLoadingId,
    },
  } = useLoadingIds()
  const header = `${programName} - ${communityName}`

  const [showDetails, setShowDetails] = useState(false)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [isAchNotAllowedModalOpen, setIsAchNotAllowedModalOpen] =
    useState(false)
  const [lcdBillingDetails, setLcdBillingDetails] =
    useState<FetchLicensedDirectorBillingDetailsResponse>()

  const fetchLicensedDirectorBillingDetails = async () => {
    try {
      const fetchedBillingDetails =
        await paymentsApi.fetchLicensedDirectorBillingDetails({
          programKey,
        })

      setLcdBillingDetails(fetchedBillingDetails)
    } catch (error) {
      await handleError(error)
    }
  }

  const { triggerFetch, isLoading, addLoadingIds } = useLoadingContext({
    loadingId: fetchLicensedDirectorBillingDetailsLoadingId,
    asyncFunction: fetchLicensedDirectorBillingDetails,
  })

  useEffect(triggerFetch, [triggerFetch, programKey])

  useEffect(() => {
    // reset the state when a different row is selected on LCDLicensingTable
    setShowDetails(false)
  }, [programKey])

  const {
    totalOwed = { amount: '0.00', currencyCode: 'USD' },
    totalPaid = { amount: '0.00', currencyCode: 'USD' },
    licensingDue = { amount: '0.00', currencyCode: 'USD' },
    firstName,
    lastName,
    actorKey,
  } = lcdBillingDetails?.director ?? {}

  const balance = convertTwoDecimalStringToBig(totalOwed.amount).minus(
    totalPaid.amount
  )
  const balanceFormatted = formatBigFixedTwoDecimals(balance)

  const triggerRefetchForBillingAndLcdData = () => {
    addLoadingIds([
      fetchLicensedDirectorBillingDetailsLoadingId,
      billingDataLoadingId,
      fetchLicensingLcdPaymentsLoadingId,
    ])
  }

  if (isLoading) {
    return <ActionableTableSkeleton />
  }

  return (
    <>
      <SpaceBetweenSection
        left={<Header id={header} headerName={header} component="h6" />}
        right={
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
              mt: 1,
              alignItems: {
                sx: 'stretch',
                sm: 'center',
              },
              gap: 1,
              width: '100%',
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <OutlinedButton
                fullWidth
                id="showDetails"
                variant={
                  !showDetails
                    ? OutlinedButtonVariant.ShowDetails
                    : OutlinedButtonVariant.HideDetails
                }
                onClick={() => setShowDetails((prev) => !prev)}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <ContainedButton
                fullWidth
                id="makeAPayment"
                disabled={!balance.gt(0)}
                variant={ContainedButtonVariant.MakeAPayment}
                onClick={() => {
                  if (achAllowed) {
                    setIsPaymentModalOpen(true)
                  } else {
                    setIsAchNotAllowedModalOpen(true)
                  }
                }}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <IconTextButton
                id="printReports"
                variant={IconTextButtonVariant.LicensingFeeReport}
                startIcon={<PrintIcon />}
                onClick={() =>
                  navigate(`/account/billing/report/lcd/${programKey}`)
                }
              />
            </Box>
          </Box>
        }
        containerStyles={{}}
      />
      {showDetails ? (
        <LCDLicensingBreakdownDetails
          breakdown={lcdBillingDetails?.director.breakdown ?? []}
          totalOwed={totalOwed.amount}
          totalPaid={totalPaid.amount}
          licensingDue={licensingDue.amount}
          balance={balanceFormatted}
          currencyCode={totalOwed.currencyCode}
          isChallenge={programName.includes('Challenge')}
        />
      ) : (
        <LCDLicensingBreakdownPreview
          totalOwed={totalOwed.amount}
          totalPaid={totalPaid.amount}
          licensingDue={licensingDue.amount}
          balance={balanceFormatted}
          currencyCode={totalOwed.currencyCode}
          lcdName={fullName({ firstName, lastName })}
        />
      )}
      <LCDLicensingBreakdownTutor
        tutors={lcdBillingDetails?.tutors ?? []}
        programName={programName}
        programKey={programKey}
        communityName={lcdBillingDetails?.community as string}
        directorName={fullName(lcdBillingDetails?.director)}
        loadingIdsToAddOnComplete={[
          fetchLicensedDirectorBillingDetailsLoadingId,
          billingDataLoadingId,
        ]}
      />
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false)
          triggerRefetchForBillingAndLcdData()
        }}
        variant={PaymentModalVariant.Licensing}
        programKey={programKey}
        actorKey={actorKey}
        amountDue={{
          amount: formatBigFixedTwoDecimals(
            convertTwoDecimalStringToBig(totalOwed.amount).minus(
              totalPaid.amount
            )
          ),
          currencyCode: totalOwed.currencyCode,
        }}
        achDiscount={{
          amount: achDiscount,
          applyDiscountTimes: 0, // Don't apply for Licensing
        }}
      />
      <BasicModal
        isOpen={isAchNotAllowedModalOpen}
        maxWidth={'xs'}
        handleFormSubmit={(event: React.FormEvent<HTMLDivElement>) => {
          event.preventDefault()
          setIsAchNotAllowedModalOpen(false)
        }}
        dialogActions={
          <ActionButtons primaryButtonLabel={ContainedButtonVariant.Ok} />
        }
        dialogContent={
          <Box maxWidth={300} mx="auto" textAlign="center">
            <Typography component="p">
              {t(
                'LCDLicensingBreakdown.ConfirmationModal.Information.AchAllowed',
                'Please contact your country coordinator directly to make your payment or email accounting directly at accounting@classicalconversations.com.'
              )}
            </Typography>
          </Box>
        }
      />
    </>
  )
}

export default LCDLicensingBreakdown
